import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BeforeAfter from "@/components/BeforeAfter";
import ReviewCard from "@/components/ReviewCard";

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14" data-testid="gallery-page">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Real results</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-slate-100 max-w-3xl">
          Drag the line. See the difference.
        </h1>
        <p className="mt-4 text-base text-slate-400 max-w-xl">Every pair below is a real customer car, shot on the day by CJ.</p>

        {items && items.length === 0 && (
          <div className="cj-card mt-10 p-10 text-center text-slate-400" data-testid="gallery-empty">
            <Sparkles className="h-8 w-8 text-[#ff6b00] mx-auto" />
            <div className="mt-3 font-display uppercase font-bold text-slate-200">Fresh showcase coming soon</div>
          </div>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 grid gap-8 md:grid-cols-2 content-start">
            {(items || []).map((it, i) => (
              <div key={it.id} className="cj-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`gallery-item-${it.id}`}>
                <BeforeAfter item={it} className="aspect-[4/3]" />
                <div className="mt-3 flex items-start justify-between gap-4">
                  <div>
                    {it.caption && <div className="font-display uppercase font-bold text-lg text-slate-100">{it.caption}</div>}
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                      {[it.vehicle_label, it.service_name].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-1" data-testid="gallery-reviews">
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">What customers say</div>
              <div className="mt-2 flex items-end gap-3">
                <div className="font-display text-4xl font-extrabold text-slate-100" data-testid="reviews-average">{avg || "—"}</div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 pb-2">{reviews.length} review{reviews.length !== 1 && "s"}</div>
              </div>
              <div className="mt-5 space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {reviews.length === 0 && <div className="cj-card p-5 text-sm text-slate-400" data-testid="reviews-empty">First reviews land after our next completed detail.</div>}
                {reviews.map((r) => <ReviewCard key={r.id} review={r} compact />)}
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-14 flex justify-center">
          <Link to="/book" data-testid="gallery-cta-book-button" className="cj-btn-primary px-8 py-4 rounded-full inline-flex items-center gap-2">
            Get this result <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
